import axios from 'axios';
import { debounce } from 'lodash';
import { useTranslation } from 'react-i18next';
import HTMLReactParser from 'html-react-parser';
import { Link, useLocation } from 'react-router-dom';
import React, { useCallback, useEffect, useState } from 'react';
// api
import BaseApi from '../api/BaseApi';
// components
import Footer from '../element/Footer';
import NavBar from '../element/NavBar';
import SmallLoading from '../element/SmallLoading';
// helpers
import { addBreadcrumbSchema, resultInfo } from '../helpers';
// mui
import CloseIcon from '@mui/icons-material/Close';

const SearchByRegion = () => {
  const location = useLocation();
  const { t } = useTranslation('global');

  const today = new Date().toLocaleDateString();

  const decodeKeyword = encodedKeyword => decodeURIComponent(encodedKeyword);

  const [filter, setFilter] = useState(null);
  const [loading, setLoading] = useState(true);
  const [inputValue, setInputValue] = useState('');
  const [industries, setIndustries] = useState(null);
  const [lettersData, setLettersData] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [regionsData, setRegionsData] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [companiesData, setCompaniesData] = useState([]);
  const [smallLoading, setSmallLoading] = useState(false);
  const [filterButton, setFilterButton] = useState('byCompanyName');
  const [formTabs, setFormTabs] = useState(location.pathname === '/search-by-regions' ? 1 : location.pathname === '/search-by-cities' ? 2 : 3);

  const handleSearchChange = useCallback(e => {
    const { value } = e.target;

    setInputValue(value);
    debouncedHandleSearchChange(value);
  }, []); // eslint-disable-line

  const handleFilterSelect = value => {
    setInputValue('');
    setSearchQuery('');
    setFilterButton('byCompanyName');
    setFilter(value);
  };

  const handlePageChange = async page => {
    window.scrollTo(0, 0);

    try {
      setSmallLoading(true);
      setPagination(prev => ({
        ...prev,
        current_page: page,
      }));

      const response = await axios.get(
        BaseApi + '/job/by-companies',
        {
          params: {
            page,
            search: searchQuery ? searchQuery : null,
            per_page: pagination ? pagination.per_page : 30,
          },
        },
      );

      if (response.data.status === 200) {
        setPagination(response.data.meta);
        setCompaniesData(response.data.data);
      } else {
        resultInfo('error', response.data.message, t('employerCreateJob.close'));
      }

      setSmallLoading(false);
    } catch (error) {
      setSmallLoading(false);

      console.error('Error fetching Jobs List', error);
    }
  };

  const debouncedHandleSearchChange = debounce(value => {
    // setCurrentPage(1);
    setSearchQuery(value);
  }, 500);

  useEffect(() => {
    const getData = async () => {
      try {
        if (formTabs === 1) {
          setSmallLoading(true);

          const response = await axios.post(BaseApi + '/jobs/by-regions');

          setSmallLoading(false);
          setRegionsData(response.data.response);
        } else if (formTabs === 2) {
          setSmallLoading(true);

          const response = await axios.post(BaseApi + '/jobs/by-letters');

          setSmallLoading(false);
          setLettersData(response.data.response);
        } else if (formTabs === 3) {
          setSmallLoading(true);

          const response = await axios.get(
            BaseApi + '/job/by-companies',
            {
              params: {
                page: 1,
                per_page: 30,
                search: searchQuery ? searchQuery : null,
                filter: {
                  industry_id: filter ? filter.id : null,
                },
              },
            },
          );

          window.scrollTo(0, 0);
          setSmallLoading(false);
          setPagination(response.data.meta);
          setCompaniesData(response.data.data);
          setIndustries(response.data.industries);
        }

        setLoading(false);
      } catch (error) {
        setLoading(false);
        setSmallLoading(false);

        console.error('Error fetching data', error);
      }
    };

    getData();
  }, [t, filter, formTabs, searchQuery]);

  useEffect(() => {
    const metaTitle = document.querySelector('title');
    const metaDescription = document.querySelector('meta[name="description"]');

    if (metaTitle) {
      metaTitle.textContent = location.pathname === '/search-by-companies'
        ? `Каталог компаній України. Більше ${pagination?.total} роботодавців України на ${today} | GRC.ua`
        : t('searchJobPage.metaTitleValue');
    }

    if (metaDescription) {
      metaDescription.content = location.pathname === '/search-by-companies'
        ? 'Повний каталог компаній, що надають роботу в Україні. Знайдіть компанію своєї мрії у каталозі на сайті GRC.ua'
        : t('searchJobPage.metaDescriptionValue');
    }

    window.scrollTo(0, 0);
  }, [location.pathname, pagination?.total, t, today]);

  useEffect(() => {
    const canonicalUrl = `${window.location.origin}${location.pathname}`;

    let linkElement = document.querySelector('link[rel="canonical"]');

    if (linkElement) {
      linkElement.setAttribute('href', canonicalUrl);
    } else {
      linkElement = document.createElement('link');
      linkElement.setAttribute('rel', 'canonical');
      linkElement.setAttribute('href', canonicalUrl);
      document.head.appendChild(linkElement);
    }

    const breadcrumbs = [
      { name: location.pathname === '/search-by-companies' ? t('searchByRegion.searchJobsByCompanies') : t('searchByRegion.searchJobsByRegions') },
    ];

    const script = addBreadcrumbSchema(breadcrumbs);

    return () => {
      if (script) document.head.removeChild(script);
    };
  }, [location.pathname, t]);

  useEffect(() => {
    const hash = location.hash.replace('#', '');
    if (hash) {
      const element = document.getElementById(decodeKeyword(hash));

      if (element) {
        const yOffset = -100;
        const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;

        window.scrollTo({ top: y, behavior: 'smooth' });
      }
    }
  }, [location, regionsData]);

  return (
    <>
      <NavBar />
      {
        loading
          ? (
            <div className='loader-container'></div>
          ) : (
            <>
              <div className='aboutusSection1 text-center'>
                <h1>
                  {formTabs === 3 ? t('searchByRegion.searchJobsByCompanies') : t('searchByRegion.searchJobsByRegions')}
                </h1>
                <h6 className='text-muted fw-normal text-center'>
                  <Link to='/' style={{ color: 'grey' }}>
                    {t('navHeaders.home')}
                  </Link>
                  {' / '}
                  {formTabs === 3 ? t('searchByRegion.searchJobsByCompanies') : t('searchByRegion.searchJobsByRegions')}
                </h6>
              </div>
              <div className='SJPSection2 container'>
                <div className='FBitem'>
                  <div className='mb-4 searchByFormTabs'>
                    <Link to='/search-by-regions'>
                      <button
                        type='button'
                        className={formTabs === 1 ? 'active' : ''}
                        onClick={() => {
                          setFormTabs(1);
                          setLettersData([]);
                          setCompaniesData([]);
                        }}
                      >
                        {t('searchByRegion.byRegions')}
                      </button>
                    </Link>
                    <Link to='/search-by-cities'>
                      <button
                        type='button'
                        className={formTabs === 2 ? 'active' : ''}
                        onClick={() => {
                          setFormTabs(2);
                          setRegionsData([]);
                          setCompaniesData([]);
                        }}
                      >
                        {t('searchByRegion.byCity')}
                      </button>
                    </Link>
                    <Link to='/search-by-companies'>
                      <button
                        type='button'
                        className={formTabs === 3 ? 'active' : ''}
                        onClick={() => {
                          setFormTabs(3);
                          setLettersData([]);
                          setRegionsData([]);
                        }}
                      >
                        {t('searchByRegion.byCompanies')}
                      </button>
                    </Link>
                  </div>
                  <div style={{ minHeight: '100px' }}>
                    {
                      formTabs === 1 && (
                        smallLoading
                          ? (
                            <div className='text-center'>
                              <SmallLoading />
                            </div>
                          ) : (
                            regionsData?.map(i => (
                              <div key={i.region} className='searchByRegionsBox'>
                                <h2 id={i.region}>{i.region}</h2>
                                <div className='row'>
                                  {
                                    i.cities.map(city => (
                                      <div key={city.name} className='mb-3 mb-md-3 col-md-3'>
                                        <Link to={`/searchjob/r-${city.name}`}>
                                          {city.name}
                                          {city.count != 0 && ` (${city.count})`}
                                        </Link>
                                      </div>
                                    ))
                                  }
                                </div>
                              </div>
                            ))
                          )
                      )
                    }
                    {
                      formTabs === 2 && (
                        smallLoading
                          ? (
                            <div className='text-center'>
                              <SmallLoading />
                            </div>
                          ) : (
                            lettersData
                              ?.sort((a, b) => a.char.localeCompare(b.char))
                              .map(i => (
                                <div key={i.char} className='searchByRegionsBox'>
                                  <h2>{i.char}</h2>
                                  <div className='row'>
                                    {
                                      i.cities.map(city => (
                                        <div key={city.name} className='mb-3 mb-md-3 col-md-3'>
                                          <Link to={`/searchjob/r-${city.name}`}>
                                            {city.name}
                                            {city.count != 0 && ` (${city.count})`}
                                          </Link>
                                        </div>
                                      ))
                                    }
                                  </div>
                                </div>
                              ))
                          )
                      )
                    }
                    {
                      formTabs === 3 && (
                        <div className='searchByCompanies'>
                          <div className='searchByCompaniesFilterBox'>
                            <div className='searchByCompaniesFilterButtons'>
                              <button
                                type='button'
                                onClick={() => setFilterButton('byCompanyName')}
                                className={`btn ${filterButton === 'byCompanyName' ? 'active' : ''}`}
                              >
                                {t('searchByRegion.byCompanyName')}
                              </button>
                              <button
                                type='button'
                                onClick={() => setFilterButton('byIndustry')}
                                className={`btn ${filterButton === 'byIndustry' ? 'active' : ''}`}
                              >
                                {t('searchByRegion.byIndustry')}
                              </button>
                            </div>
                            {
                              filterButton === 'byCompanyName'
                                ? (
                                  <input
                                    type='text'
                                    name='searchQuery'
                                    value={inputValue}
                                    className='form-control'
                                    onChange={handleSearchChange}
                                    placeholder={t('searchByRegion.enterCompanyName')}
                                  />
                                ) : ''
                            }
                          </div>
                          {
                            filter
                              ? (
                                <p className='searchByCompaniesFilterSelected'>
                                  <span className='me-2 fw-bold'>{t('searchByRegion.selectedIndustry')}:</span>
                                  <span className='d-flex align-items-center'>
                                    {filter.name}
                                    <button
                                      type='button'
                                      className='ms-2 ms-lg-0'
                                      onClick={() => setFilter(null)}
                                    >
                                      <CloseIcon />
                                    </button>
                                  </span>
                                </p>
                              ) : ''
                          }
                          {
                            smallLoading
                              ? (
                                <div className='text-center'>
                                  <SmallLoading />
                                </div>
                              ) : (
                                filterButton === 'byCompanyName'
                                  ? (
                                    <div className='searchByCompaniesList'>
                                      {
                                        companiesData?.map(i => (
                                          <Link key={i.id} to={`/company/${i.employer_id}`}>
                                            <div className='searchByCompaniesBox'>
                                              <div>
                                                <div className='companyTitleInfo'>
                                                  <h2 id={i.slug}>{i.company_name}</h2>
                                                  <p className='count'>{t('searchByRegion.vacancies')} - {i.jobs_count}</p>
                                                </div>
                                                <div className='companyAbout standartListStyle'>
                                                  {/* {i.company_about ? HTMLReactParser(i.company_about) : ''} */}
                                                  {
                                                    !i.company_about
                                                      ? ''
                                                      : (
                                                        i.company_about?.length > 500
                                                          ? HTMLReactParser(`${i.company_about.slice(0, 500)} ...`)
                                                          : HTMLReactParser(i.company_about)
                                                      )
                                                  }
                                                </div>
                                              </div>
                                              <div className='companyLogo'>
                                                <img
                                                  width='104px'
                                                  alt={i.company_name}
                                                  src={i.company_logo ? i.company_logo : '/Images/jobseekerSide/dummy-profile.png'}
                                                />
                                              </div>
                                            </div>
                                          </Link>
                                        ))
                                      }
                                      <div className='manageJobPagination'>
                                        <p className='text-muted empPaginationData'>
                                          {t('pagination.NoofRecords')}
                                          {' '}
                                          {pagination?.from ? pagination?.from : 0} - {pagination?.to ? pagination?.to: 0}
                                          {' '}
                                          {t('pagination.of')} {pagination?.total ? pagination?.total : 0}
                                        </p>
                                        <div className='d-flex justify-content-center empPaginationButton'>
                                          <button
                                            className='btn navButton1 me-2'
                                            disabled={pagination?.current_page === 1}
                                            onClick={() => handlePageChange(pagination?.current_page - 1)}
                                          >
                                            {t('pagination.Prev')}
                                          </button>
                                          <button
                                            className='btn navButton1'
                                            disabled={pagination?.to >= pagination?.total}
                                            onClick={() => handlePageChange(pagination?.current_page + 1)}
                                          >
                                            {t('pagination.Next')}
                                          </button>
                                        </div>
                                      </div>
                                    </div>
                                  ) : (
                                    <div className='row byIndustry'>
                                      {
                                        industries?.map(i => (
                                          <div key={i.id} className='d-block col-md-6 mb-3'>
                                            <Link onClick={() => handleFilterSelect(i)}>
                                              {i.name}
                                            </Link>
                                          </div>
                                        ))
                                      }
                                    </div>
                                  )
                              )
                          }
                        </div>
                      )
                    }
                  </div>
                </div>
              </div>
              <Footer />
            </>
          )
      }
    </>
  );
};

export default SearchByRegion;
